import {
  CollectionSubType,
  type EntitySubtype,
  EntityType,
  EpisodeSubType,
  ReactQueryStorageKey,
  ShowSubtype,
  ViewType,
} from '@audacy-clients/client-services/core';
import AudacyLogger from '@audacy-clients/client-services/src/AudacyLogger';
import { featureFlagMap } from '@audacy-clients/client-services/src/utils';
import {
  _defaultSubNavigationTabs,
  getSubNavigationConfigurationMap,
} from '@audacy-clients/core/atoms/config/subNavigation';
import { isInternetReachableState } from '@audacy-clients/core/atoms/isInternetReachable';
import clientServices, { getClientServices } from '@audacy-clients/core/utils/clientServices';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { z } from 'zod';
import { queryKeys } from '../helpers/api';
import { noCacheDefaultSelector } from '../helpers/noCacheDefaultSelector';
import { type ViewIDKey } from './constants';

export type IViewInfo = {
  viewId: string | ViewIDKey;
  viewType: ViewType;
};

// Zod schemas for validation
const ITabNavigationSchema = z.object({
  iconRef: z.string(),
  label: z.string(),
  tabKey: z.string(),
  viewId: z.string(),
  viewType: z.nativeEnum(ViewType),
});

const TabsSchema = z.array(ITabNavigationSchema);

const IViewInfoSchema = z.object({
  viewId: z.string(),
  viewType: z.nativeEnum(ViewType),
});

const IViewMapSchema = z.record(z.string(), z.record(z.string(), IViewInfoSchema));

export type ITabNavigation = {
  iconRef: string;
  label: string;
  tabKey: string;
} & IViewInfo;

export type IViewMap = Record<string, Record<string, IViewInfo>>;

export type IClientSubNavigation = {
  tabs: Array<ITabNavigation>;
  viewMap: IViewMap;
};

export type IClientNavigation = {
  tabs: Array<ITabNavigation>;
  viewMap: IViewMap;
};

export const defaultViewMap: IViewMap = {
  [EntityType.EPISODE]: {
    [EpisodeSubType.BROADCAST_SHOW_EPISODE]: {
      viewId: 'episode',
      viewType: ViewType.EPISODE,
    },
    [EpisodeSubType.HOST_CREATED_EPISODE]: {
      viewId: 'episode',
      viewType: ViewType.EPISODE,
    },
    [EpisodeSubType.PODCAST_EPISODE]: {
      viewId: 'episode',
      viewType: ViewType.EPISODE,
    },
  },
  [EntityType.SHOW]: {
    [ShowSubtype.BROADCAST]: {
      viewId: 'shows',
      viewType: ViewType.SHOW,
    },
    [ShowSubtype.DIGITAL]: {
      viewId: 'shows',
      viewType: ViewType.SHOW,
    },
    [ShowSubtype.HOST_CREATED]: {
      viewId: 'shows',
      viewType: ViewType.SHOW,
    },
    [ShowSubtype.PODCAST]: {
      viewId: 'podcasts',
      viewType: ViewType.SHOW,
    },
  },
  [EntityType.COLLECTION]: {
    [CollectionSubType.PLAYLIST]: {
      viewId: 'playlists',
      viewType: ViewType.PLAYLIST,
    },
  },
};

const CACHE_TIME = 4 * 60 * 60 * 1000; // 4 hours in milliseconds

export const loadRemoteSubNavigation = async (viewId: string): Promise<IClientSubNavigation> => {
  const subNavigationConfiguration = getSubNavigationConfigurationMap();
  const id = subNavigationConfiguration.get(viewId)?.route;

  if (!id) {
    return {
      tabs: _defaultSubNavigationTabs,
      viewMap: { ...defaultViewMap },
    };
  }

  const response = await getClientServices().then((cs) =>
    cs.getDataServices().getClientSubNavigation(id),
  );

  const subNavigationTabs = featureFlagMap.getAll().curatorDefinedNavigation
    ? response.tabs
    : _defaultSubNavigationTabs;

  const wrapped: IClientSubNavigation = {
    tabs: (subNavigationTabs || []).map((t) => ({
      iconRef: t.iconRef,
      label: t.label,
      tabKey: `tab:${t.viewType}:${t.viewId}`,
      viewId: t.viewId,
      viewType: t.viewType,
    })),
    viewMap: {
      ...defaultViewMap,
      ...(response.viewMap || {}),
    },
  };

  return wrapped;
};

export const loadRemoteNavigation = async (): Promise<IClientNavigation> => {
  const response = await getClientServices().then((cs) =>
    cs.getDataServices().getClientNavigationTwo(),
  );

  const wrapped: IClientNavigation = {
    tabs: (response.tabs || []).map((t) => ({
      iconRef: t.iconRef,
      label: t.label,
      tabKey: `tab:${t.viewType}:${t.viewId}`,
      viewId: t.viewId,
      viewType: t.viewType,
    })),
    viewMap: {
      ...defaultViewMap,
      ...(response.viewMap || {}),
    },
  };

  return wrapped;
};

export const webClientNavigation = noCacheDefaultSelector<IClientNavigation>({
  get: () => loadRemoteNavigation(),
  key: 'WebClientNavigation',
});

export const useViewInfoByTypeAndSubtype = (
  type?: EntityType,
  subtype?: EntitySubtype,
): IViewInfo | undefined => {
  const { viewMap } = useClientNavigation();
  if (!type || !subtype) {
    return undefined;
  }
  return viewMap[type]?.[subtype] || viewMap[type]?.default;
};

export const mapEntityToParentTypes = (
  type?: EntityType,
  subtype?: EntitySubtype,
): [EntityType, EntitySubtype] | undefined => {
  if (type === EntityType.EPISODE) {
    if (
      subtype === EpisodeSubType.BROADCAST_SHOW_EPISODE ||
      subtype === EpisodeSubType.HOST_CREATED_EPISODE
    ) {
      return [EntityType.SHOW, ShowSubtype.BROADCAST];
    }

    if (subtype === EpisodeSubType.PODCAST_EPISODE) {
      return [EntityType.SHOW, ShowSubtype.PODCAST];
    }
  }

  if (type === EntityType.STANDALONE_CHAPTER) {
    return [EntityType.SHOW, ShowSubtype.BROADCAST];
  }

  return undefined;
};

type TGetViewInfoByTypeAndSubtype = (
  type?: EntityType,
  subtype?: EntitySubtype,
  options?: { shouldGetInfoForParent?: boolean },
) => IViewInfo | undefined;

const initialData = {
  tabs: [
    {
      iconRef: 'Logo',
      label: 'Home',
      tabKey: 'tab:MODULES:home',
      viewId: 'home',
      viewType: ViewType.MODULES,
    },
  ],
  viewMap: { ...defaultViewMap },
};

const handleRemoteNavigationSuccess = (data: IClientNavigation) => {
  clientServices
    .getPersonalizationServices()
    .dataStore.setDataSync(ReactQueryStorageKey.REMOTE_NAVIGATION, data);
};

export const useClientNavigation = (): IClientNavigation => {
  const isInternetReachable = useRecoilValue(isInternetReachableState);

  const {
    data = initialData,
    isSuccess,
    isPlaceholderData,
  } = useQuery<IClientNavigation>({
    queryKey: queryKeys.remoteNavigation(),
    queryFn: loadRemoteNavigation,
    enabled: isInternetReachable,
    placeholderData: initialData,
    gcTime: CACHE_TIME,
    refetchInterval: CACHE_TIME,
  });

  useEffect(() => {
    if (isSuccess && !isPlaceholderData) {
      const tabsResult = TabsSchema.safeParse(data.tabs);
      const viewMapResult = IViewMapSchema.safeParse(data.viewMap);

      if (!tabsResult.success) {
        AudacyLogger.error(`Failed to parse navigation tabs: ${JSON.stringify(tabsResult.error)}`);
        data.tabs = initialData.tabs;
      }

      if (!viewMapResult.success) {
        AudacyLogger.error(
          `Failed to parse navigation viewMap: ${JSON.stringify(viewMapResult.error)}`,
        );
        data.viewMap = initialData.viewMap;
      }

      if (tabsResult.success || viewMapResult.success) {
        handleRemoteNavigationSuccess(data);
      } else {
        AudacyLogger.error(
          `Failed to parse both navigation tabs and viewMap: ${JSON.stringify({
            tabsError: tabsResult.error,
            viewMapError: viewMapResult.error,
          })}`,
        );
      }
    }
  }, [isSuccess, data, isPlaceholderData]);

  return data;
};

const handleRemoteSubNavigationSuccess = (data: IClientSubNavigation) => {
  clientServices
    .getPersonalizationServices()
    .dataStore.setDataSync(ReactQueryStorageKey.REMOTE_SUB_NAVIGATION, data);
};

export const useClientSubNavigation = (viewId: string): IClientSubNavigation => {
  const isInternetReachable = useRecoilValue(isInternetReachableState);
  const queryClient = useQueryClient();

  const subNavigationQueryKeys = [queryKeys.remoteSubNavigation(), viewId];
  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: subNavigationQueryKeys });
  }, [featureFlagMap.getAll().curatorDefinedNavigation]);

  const placeholder = {
    tabs: _defaultSubNavigationTabs,
    viewMap: { ...defaultViewMap },
  };

  const {
    data = placeholder,
    isSuccess,
    isPlaceholderData,
  } = useQuery({
    queryKey: subNavigationQueryKeys,
    queryFn: () => loadRemoteSubNavigation(viewId),
    enabled: isInternetReachable,
    placeholderData: placeholder,
  });

  useEffect(() => {
    if (isSuccess && !isPlaceholderData) {
      handleRemoteSubNavigationSuccess(data);
    }
  }, [isSuccess, isPlaceholderData, data]);

  return data;
};

export const useGetViewInfoByTypeAndSubtype = (): TGetViewInfoByTypeAndSubtype => {
  const queryClient = useQueryClient();
  const { viewMap } =
    queryClient.getQueryData<IClientNavigation>(queryKeys.remoteNavigation()) ?? initialData;

  return useCallback(
    (type, subtype, { shouldGetInfoForParent } = {}) => {
      let [finalType, finalSubtype] = [type, subtype];

      if (shouldGetInfoForParent) {
        [finalType, finalSubtype] = mapEntityToParentTypes(type, subtype) || [];
      }

      if (!finalType || !finalSubtype) {
        return undefined;
      }

      return viewMap[finalType]?.[finalSubtype] || viewMap[finalType]?.default;
    },
    [viewMap],
  );
};
